import React, { useState, useEffect, useCallback } from 'react';
// Reuses PG's stucert- styling (frontend/src/components/StudentDashboard/StudentCertificates.css)
// rather than duplicating it — same cross-folder import pattern UGStudentDashboard.js
// already uses for PasswordChangeModal/ManageAccountModal.
import '../StudentDashboard/StudentCertificates.css';
import collegeConfig from '../../config/collegeConfig';
import { jsPDF } from 'jspdf';
import { savePDF } from '../../utils/savePDF';
import { FaAward, FaDownload, FaSpinner, FaChevronLeft, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const getBase64Image = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// Same landscape layout as PG's downloadElogbookCertificatePDF (see
// StudentDashboard/StudentCertificates.js) — wording swapped for an
// MBBS/UG student and a "Faculty In-Charge" + "Issued By" signature pair.
const downloadUGElogbookCertificatePDF = async (cert, collegeName, logoSrc) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const MAROON   = [142, 44,  44];
  const GOLD     = [212, 177, 90];
  const NAVY     = [31,  60,  136];
  const DARKGRAY = [60,  60,  60];

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(3);
  doc.rect(8, 8, W - 16, H - 16);
  doc.setLineWidth(1);
  doc.rect(12, 12, W - 24, H - 24);

  doc.setFillColor(...MAROON);
  doc.rect(12, 12, W - 24, 52, 'F');

  const logoBase64 = await getBase64Image(logoSrc);
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', W / 2 - 14, 13, 28, 28); }
    catch (_) { /* skip */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(collegeName, W - 60);
  doc.text(nameLines, W / 2, 48, { align: 'center' });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.line(15, 66, W - 15, 66);

  doc.setTextColor(...MAROON);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('CERTIFICATE', W / 2, 82, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(12);
  doc.setTextColor(...GOLD);
  doc.text('of E-Logbook Completion', W / 2, 90, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text('MBBS (Undergraduate)', W / 2, 99, { align: 'center' });

  const batchLabel = cert.batchLabel || '____';

  const bodyText =
    `This is to certify that ${cert.studentName}, Undergraduate (MBBS) student, Batch ${batchLabel}, at ${collegeName}, ` +
    `has successfully and satisfactorily maintained and completed the E-Logbook covering all academic activities, clinical ` +
    `postings, procedures, and case discussions as mandated by the NMC CBME curriculum. The entries recorded therein have ` +
    `been duly reviewed and verified by the concerned faculty.`;

  doc.setFont('times', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...DARKGRAY);
  const bodyLines = doc.splitTextToSize(bodyText, W - 60);
  doc.text(bodyLines, W / 2, 111, { align: 'center', lineHeightFactor: 1.6 });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(15, H - 52, W - 15, H - 52);

  const col1 = 50;
  const col2 = W / 2;
  const col3 = W - 50;
  const sigY = H - 32;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Faculty In-Charge', col1, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col1 - 30, sigY - 4, col1 + 30, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.facultyInchargeName || '', col1, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Date', col2, sigY - 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(formatDate(cert.generatedAt), col2, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Issued By', col3, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col3 - 35, sigY - 4, col3 + 35, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.issuedByName || '', col3, sigY, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Certificate No: ${cert.certificateNumber || ''}`, W / 2, H - 16, { align: 'center' });

  await savePDF(doc, `UG_Elogbook_Completion_Certificate.pdf`);
};

// ─── Component ────────────────────────────────────────────────────────────────
const UGCertificates = ({ onBack }) => {
  const [elogbookCert, setElogbookCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const token = localStorage.getItem('token');
  const API = collegeConfig.api.baseURL;

  const fetchCertificate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/ug/certificates/my-elogbook-certificate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setElogbookCert(data.certificate || null);
      }
    } catch (err) {
      console.error('Error fetching UG E-Logbook certificate:', err);
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { fetchCertificate(); }, [fetchCertificate]);

  const handleDownload = async () => {
    if (!elogbookCert) return;
    setDownloading(true);
    try { await downloadUGElogbookCertificatePDF(elogbookCert, collegeConfig.collegeName, collegeConfig.logo); }
    finally { setDownloading(false); }
  };

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaAward /> Certificates</h2>

      {loading ? (
        <div className="ug-due-empty">Loading your certificates...</div>
      ) : (
        <section className="stucert-section">
          <div className="stucert-section-header">
            <FaAward className="stucert-section-icon maroon" />
            <h3>E-Logbook Completion Certificate</h3>
          </div>

          {elogbookCert ? (
            <div className="stucert-card stucert-card-issued">
              <div className="stucert-card-status issued">
                <FaCheckCircle /> Certificate Issued
              </div>
              <div className="stucert-cert-preview">
                <div className="stucert-preview-header">
                  <img src={collegeConfig.logo} alt="College Logo" className="stucert-preview-logo" />
                  <div>
                    <p className="stucert-preview-college">{collegeConfig.collegeName}</p>
                    <p className="stucert-preview-cert-label">CERTIFICATE of E-Logbook Completion</p>
                  </div>
                </div>
                <p className="stucert-preview-course">MBBS (Undergraduate)</p>
                <p className="stucert-preview-body">
                  This is to certify that <strong>{elogbookCert.studentName}</strong>, Undergraduate (MBBS) student
                  {elogbookCert.batchLabel ? `, Batch ${elogbookCert.batchLabel}` : ''}, at {collegeConfig.collegeName}, has
                  successfully and satisfactorily maintained and completed the E-Logbook covering all academic
                  activities, clinical postings, procedures, and case discussions as mandated by the NMC CBME
                  curriculum. The entries recorded therein have been duly reviewed and verified by the concerned faculty.
                </p>
                <div className="stucert-preview-meta">
                  {elogbookCert.facultyInchargeName && (
                    <span>Faculty In-Charge: <strong>{elogbookCert.facultyInchargeName}</strong></span>
                  )}
                  <span>Issued By: <strong>{elogbookCert.issuedByName}</strong></span>
                  <span>Date: <strong>{formatDate(elogbookCert.generatedAt)}</strong></span>
                </div>
                <p className="stucert-cert-number">Certificate No: {elogbookCert.certificateNumber}</p>
              </div>
              <button className="stucert-download-btn" onClick={handleDownload} disabled={downloading}>
                {downloading ? <><FaSpinner className="stucert-spinner-sm" /> Generating PDF...</> : <><FaDownload /> Download as PDF</>}
              </button>
            </div>
          ) : (
            <div className="stucert-card stucert-card-pending">
              <div className="stucert-card-status pending">
                <FaTimesCircle /> Not Yet Issued
              </div>
              <p className="stucert-pending-msg">
                Your E-Logbook completion certificate has not been generated yet. It will be issued by your faculty once your E-Logbook is complete.
              </p>
            </div>
          )}
        </section>
      )}
    </main>
  );
};

export default UGCertificates;
